import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogCard from '@/pages/blog/components/BlogCard';
import { authors, blogArticles } from '@/mocks/blog';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function AuthorPage() {
  const { slug } = useParams();
  const author = authors.find((a) => a.slug === slug) || authors[0];
  const authorArticles = blogArticles.filter((a) => a.authorSlug === author.slug);

  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24 pb-20">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-400 transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/blog" className="hover:text-primary-400 transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-400">Auteur</span>
          </nav>
        </div>

        {/* Author Header */}
        <section className="w-full px-4 md:px-8 lg:px-12 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
              <div className="shrink-0">
                <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden">
                  <img
                    src={author.image}
                    alt={`${author.name} — portrait professionnel`}
                    className="w-full h-full object-cover"
                    title={`${author.name} — portrait`}
                  />
                </div>
              </div>
              <div className="text-center md:text-left flex-1">
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-2">
                  {author.name}
                </h1>
                <p className="text-sm font-medium text-primary-400 mb-3">{author.role}</p>
                <div className="flex items-center justify-center md:justify-start gap-2 text-sm text-foreground-400 mb-4">
                  <i className="ri-map-pin-line" />
                  <span>{author.location}</span>
                </div>
                <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                  {author.bio}
                </p>
                <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                  {author.expertise.map((exp) => (
                    <span key={exp} className="px-3 py-1 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium border border-primary-400/20">
                      {exp}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-center md:justify-start gap-2">
                  {author.socials.twitter && (
                    <a
                      href={author.socials.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-400 hover:text-primary-400 hover:border-primary-400/40 transition-colors"
                      aria-label="Twitter"
                    >
                      <i className="ri-twitter-x-line" />
                    </a>
                  )}
                  {author.socials.linkedin && (
                    <a
                      href={author.socials.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-400 hover:text-primary-400 hover:border-primary-400/40 transition-colors"
                      aria-label="LinkedIn"
                    >
                      <i className="ri-linkedin-line" />
                    </a>
                  )}
                  {author.socials.website && (
                    <a
                      href={author.socials.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-400 hover:text-primary-400 hover:border-primary-400/40 transition-colors"
                      aria-label="Site web"
                    >
                      <i className="ri-global-line" />
                    </a>
                  )}
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-500/10 text-secondary-400 text-xs font-medium border border-secondary-400/20 hover:bg-secondary-500/20 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-user-line" />
                    À propos
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Articles by author */}
        <section ref={ref} className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-2">
              Articles de {author.name.split(' ').slice(-2).join(' ')}
            </h2>
            <p className="text-sm text-foreground-400 mb-8">
              {authorArticles.length} article{authorArticles.length > 1 ? 's' : ''} publié{authorArticles.length > 1 ? 's' : ''}
            </p>

            {authorArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-article-line text-2xl text-foreground-400" />
                </div>
                <p className="text-foreground-500 font-medium">Aucun article pour le moment</p>
              </div>
            ) : (
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                {authorArticles.map((article) => (
                  <BlogCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default AuthorPage;